import * as React from "react";
import { cn } from "./cn.js";

const FIELD_BASE = cn(
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
  "ring-offset-background placeholder:text-muted-foreground",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive",
);

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = "text", ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(FIELD_BASE, "h-9 file:border-0 file:bg-transparent file:text-sm", className)}
      {...props}
    />
  );
});

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, ...props },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={cn(FIELD_BASE, className)} {...props} />;
});

export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean };

/**
 * A real <label>. Every field in this app is labelled with one — placeholders
 * are never used as labels.
 */
export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(function Label(
  { className, children, required = false, ...props },
  ref,
) {
  return (
    <label
      ref={ref}
      className={cn("text-sm font-medium leading-none text-foreground", className)}
      {...props}
    >
      {children}
      {required ? (
        <>
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </label>
  );
});

export interface FieldProps {
  id: string;
  label: React.ReactNode;
  /** Rendered under the control and wired up via aria-describedby. */
  hint?: React.ReactNode;
  /** Rendered with role="alert" and wired up via aria-describedby. */
  error?: string | undefined;
  required?: boolean;
  className?: string;
  children: (aria: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
  }) => React.ReactNode;
}

/**
 * Label + control + hint + error, with the aria wiring done once so no screen
 * can forget it. The control is supplied as a render prop and receives the ids.
 */
export function Field({
  id,
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })}
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
